"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault").default;
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.Control = void 0;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _core = require("@emotion/core");
var _lodestarUtils = require("@smartsheet/lodestar-utils");
var _sdsThemeInternal = require("@smartsheet/sds-theme-internal");
var _classnames = _interopRequireDefault(require("classnames"));
var _react = require("react");
var _reactSelect = require("react-select");
var _borders = require("../../../constants/borders");
var _colors = require("../../../constants/colors");
var _useTheme = require("../../../theme/utils/useTheme");
var _focusRing = require("../../../util/focusRing");
var _IE11Flexbug3Fix = require("./IE11Flexbug3Fix");
var _LayoutContext = require("./LayoutContext");
var _StaticPropContext = require("./StaticPropContext");
var _ValidationContext = require("./ValidationContext");
/** @jsxRuntime classic */
/** @jsx jsx */

const controlTheme = (0, _sdsThemeInternal.themed)({
  borderRadius: {
    "lodestar-light": (0, _borders.borderRadius)() + "px",
    "ursa-light": (0, _borders.borderRadius)() + "px"
  },
  border: {
    "lodestar-light": "none",
    "ursa-light": "none"
  },
  padding: {
    "lodestar-light": 4,
    "ursa-light": 4
    // To account for the added 1px border on all sides.
  },

  background: {
    menuIsOpen: {
      "lodestar-light": _colors.neutralLight40,
      "ursa-light": _colors.neutralLight40
    },
    menuIsClosed: {
      "lodestar-light": _colors.neutralLight30,
      "ursa-light": _colors.neutralLight30
    }
  },
  backgroundActive: {
    "lodestar-light": _colors.neutral,
    "ursa-light": _colors.neutral
  },
  backgroundHover: {
    menuIsOpen: {
      "lodestar-light": _colors.neutralLight40,
      "ursa-light": _colors.neutralLight40
    },
    menuIsClosed: {
      "lodestar-light": _colors.neutralLight20,
      "ursa-light": _colors.neutralLight20
    }
  },
  textColor: {
    menuIsOpen: _colors.neutralDark30,
    menuIsClosed: _colors.neutralDark40
  }
});
const ringTheme = (0, _sdsThemeInternal.themed)({
  focus: {
    outlineColor: {
      default: {
        "lodestar-light": _colors.focus,
        "ursa-light": _colors.focus
      },
      error: {
        "lodestar-light": _colors.danger,
        "ursa-light": _colors.danger
      }
    },
    radius: {
      "lodestar-light": 7,
      "ursa-light": 7
    },
    offset: {
      "lodestar-light": 4,
      "ursa-light": 4
    }
  },
  active: {
    outlineColor: {
      default: {
        "lodestar-light": _colors.focus,
        "ursa-light": _colors.focus
      },
      error: {
        "lodestar-light": _colors.danger,
        "ursa-light": _colors.danger
      }
    },
    radius: {
      "lodestar-light": 4,
      "ursa-light": 4
    },
    offset: {
      "lodestar-light": 0,
      "ursa-light": 0
    }
  }
});
const Control = _ref => {
  let {
    children,
    innerRef: providedRef,
    className,
    ...props
  } = _ref;
  const layoutContext = (0, _LayoutContext.useLayoutContext)();
  const validationState = (0, _react.useContext)(_ValidationContext.ValidationContext);
  const {
    clientId
  } = (0, _react.useContext)(_StaticPropContext.StaticPropContext);
  const innerRef = (0, _lodestarUtils.useEnsuredForwardedRef)(providedRef);

  // Using a normal effect here because though we're measuring the DOM and updating layout here,
  // the component that will be affected is the MenuPortal, not the Control.
  (0, _react.useEffect)(() => {
    // If the control's height isn't the same as the layoutContext, notify!
    const height = innerRef.current?.offsetHeight;
    if (height && height !== layoutContext.controlHeight) {
      layoutContext.setControlHeight(height);
    }
  });
  const {
    key
  } = (0, _useTheme.useTheme)();
  const theme = key;
  const focusRing = (0, _react.useMemo)(() => (0, _focusRing.getFocusRingStyles)({
    theme,
    offset: ringTheme.focus.offset(theme),
    radius: ringTheme.focus.radius(theme)
  }), [theme]);
  const activeRing = (0, _react.useMemo)(() => (0, _focusRing.getFocusRingStyles)({
    theme,
    colorOverride: validationState === "error" ? ringTheme.active.outlineColor.error(theme) : ringTheme.active.outlineColor.default(theme),
    offset: ringTheme.active.offset(theme),
    radius: ringTheme.active.radius(theme),
    pseudoElement: "after"
  }), [theme, validationState]);
  const menuIsOpen = !!props.selectProps.menuIsOpen;
  return (0, _core.jsx)(_IE11Flexbug3Fix.IE11Flexbug3Fix, null, (0, _core.jsx)(_reactSelect.components.Control, (0, _extends2.default)({}, props, {
    innerProps: {
      ...props.innerProps,
      // Yarg! React-select spreads this object onto the Control but doesn't allow an arbitrary shape.
      // Let's just push through this since it's a safe thing to do in this case.
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      "data-client-id": clientId && `${clientId}--control`
    },
    innerRef: innerRef,
    className: (0, _classnames.default)(_focusRing.FOCUS_USING_BEFORE_CLASS_NAME, className),
    css: (0, _core.css)`
                    border-radius: ${controlTheme.borderRadius(theme)};
                    border: ${controlTheme.border(theme)};
                    background: ${menuIsOpen ? controlTheme.background.menuIsOpen(theme) : controlTheme.background.menuIsClosed(theme)};
                    color: ${menuIsOpen ? controlTheme.textColor.menuIsOpen(theme) : controlTheme.textColor.menuIsClosed(theme)};
                    box-shadow: none;
                    padding: ${controlTheme.padding(theme)}px;
                    transition: background 0.2s;
                    cursor: pointer;
                    min-height: 40px;

                    &:hover {
                        background: ${menuIsOpen ? controlTheme.backgroundHover.menuIsOpen(theme) : controlTheme.backgroundHover.menuIsClosed(theme)};
                        border: ${controlTheme.border(theme)};
                    }
                    &:active {
                        border-color: transparent;
                        background: ${controlTheme.backgroundActive(theme)};
                    }

                    ${!menuIsOpen && props.isFocused && focusRing}

                    ${(validationState === "error" || menuIsOpen) && activeRing}
                `
  }), children));
};
exports.Control = Control;
//# sourceMappingURL=Control.js.map